import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Search, FileText, CheckCircle2, Truck, ShieldCheck, Network, Handshake,
  Phone, Mail, MapPin, Clock, PackageCheck, Boxes,
} from "lucide-react";
import api from "@/lib/api";
import { LegalText, VersionLine } from "@/components/LegalDialog";

const LEGAL = ["gizlilik", "aydinlatma"];
const telHref = (t) => `tel:${(t || "").replace(/[^\d+]/g, "").replace(/^0/, "+90")}`;

const IMG = {
  hero_lab: "https://images.unsplash.com/photo-1554475901-4538ddfbccc2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  warehouse: "https://images.unsplash.com/photo-1592085198739-ffcad7f36b54?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  food_qc: "https://images.unsplash.com/photo-1551884170-09fb70a3a2ed?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  analysis: "https://images.unsplash.com/photo-1605781231474-f60dea478e8a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
};

const CONFIG = {
  hakkimizda: { hero: IMG.hero_lab, kicker: "Kurumsal" },
  "tedarik-sureci": { hero: IMG.warehouse, kicker: "Nasıl Çalışırız" },
  iletisim: { hero: IMG.food_qc, kicker: "Bize Ulaşın" },
};

const VALUES = [
  { icon: Boxes, t: "Geniş Ürün Yelpazesi", d: "Laboratuvardan gıda hammaddesine binlerce ürün tek platformda." },
  { icon: Handshake, t: "Tek Noktadan Teklif", d: "Farklı tedarikçileri sizin için koordine eder, tek listede toplarız." },
  { icon: ShieldCheck, t: "Şeffaf & Güvenilir", d: "Açık iletişim, net fiyatlandırma ve doğrulanmış tedarik koşulları." },
  { icon: Network, t: "Koordineli Tedarik", d: "Uygun ürünlerde tedarikçiden müşteriye doğrudan sevkiyat." },
];

const STEPS = [
  { icon: Search, t: "Ürünleri Seçin", d: "Geniş katalogdan ihtiyacınıza uygun ürünleri bulun ve listeye ekleyin." },
  { icon: FileText, t: "Teklif Talebi Gönderin", d: "Ambalaj, miktar ve notlarınızla birlikte talebinizi iletin." },
  { icon: CheckCircle2, t: "Değerlendirelim", d: "Tedarik uygunluğu, güncel fiyat ve teslimat koşullarını teyit ederiz." },
  { icon: Truck, t: "Sevkiyat", d: "Onay sonrası uygun kargo/nakliye ile, tedarikçiden doğrudan sevk." },
];

export default function ContentPage({ slug: fixedSlug }) {
  const params = useParams();
  const slug = fixedSlug || params.slug;
  const [data, setData] = useState({ baslik: "", icerik: "" });
  const [company, setCompany] = useState({});
  const cfg = CONFIG[slug] || { hero: IMG.analysis, kicker: "" };

  useEffect(() => {
    api.get(`/content/${slug}`).then((r) => {
      setData(r.data);
      if (r.data.baslik) document.title = `${r.data.baslik} | Molar Kimya`;
    }).catch(() => {});
    if (slug === "iletisim")
      api.get("/settings/public").then((r) => setCompany(r.data.company || {})).catch(() => {});
    window.scrollTo(0, 0);
  }, [slug]);

  return (
    <div>
      {/* Hero */}
      <section className="relative h-64 sm:h-80 overflow-hidden bg-slate-900">
        <img src={cfg.hero} alt={data.baslik} className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-slate-900/30" />
        <div className="relative max-w-[1100px] mx-auto px-4 sm:px-6 h-full flex flex-col justify-end pb-10">
          {cfg.kicker && <span className="text-teal-300 text-sm font-semibold tracking-wide uppercase mb-2">{cfg.kicker}</span>}
          <h1 className="font-head text-3xl sm:text-5xl font-bold text-white" data-testid="content-title">{data.baslik}</h1>
        </div>
      </section>

      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-12">
        {/* Editable prose */}
        {LEGAL.includes(slug) ? (
          <div className="max-w-3xl text-base" data-testid="content-body">
            <LegalText text={data.icerik} />
            <VersionLine doc={data} />
          </div>
        ) : (
        <div className="prose prose-slate max-w-3xl text-slate-600 leading-relaxed whitespace-pre-line text-base sm:text-lg"
          data-testid="content-body">
          {data.icerik}
        </div>
        )}

        {/* Hakkımızda — value cards + stats + image */}
        {slug === "hakkimizda" && (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
              {VALUES.map((v, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 fade-up" style={{ animationDelay: `${i * 70}ms` }}>
                  <div className="w-11 h-11 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center mb-3">
                    <v.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-head font-semibold text-slate-900">{v.t}</h3>
                  <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{v.d}</p>
                </div>
              ))}
            </div>
            <div className="grid sm:grid-cols-4 gap-4 mt-8">
              {[["2005", "Kuruluş"], ["6.500+", "Katalog Ürünü"], ["33", "Sektör"], ["73", "Kullanım Alanı"]].map(([n, l]) => (
                <div key={l} className="bg-slate-900 text-white rounded-xl p-6 text-center">
                  <div className="font-head text-3xl font-bold text-teal-300">{n}</div>
                  <div className="text-sm text-slate-300 mt-1">{l}</div>
                </div>
              ))}
            </div>
            <div className="mt-10 rounded-2xl overflow-hidden border border-slate-200">
              <img src={IMG.analysis} alt="Laboratuvar analizi" className="w-full h-72 object-cover" />
            </div>
          </>
        )}

        {/* Tedarik Süreci — steps + logistics image */}
        {slug === "tedarik-sureci" && (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
              {STEPS.map((s, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 relative fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                  <div className="w-11 h-11 rounded-lg bg-teal-600 text-white flex items-center justify-center mb-4">
                    <s.icon className="w-5 h-5" />
                  </div>
                  <div className="text-xs font-semibold text-teal-600 mb-1">ADIM {i + 1}</div>
                  <h3 className="font-head font-semibold text-slate-900">{s.t}</h3>
                  <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{s.d}</p>
                </div>
              ))}
            </div>
            <div className="grid md:grid-cols-2 gap-6 mt-10 items-center">
              <div className="rounded-2xl overflow-hidden border border-slate-200">
                <img src={IMG.warehouse} alt="Lojistik ve depo" className="w-full h-64 object-cover" />
              </div>
              <div className="space-y-4">
                {[
                  { icon: PackageCheck, t: "Tedarik Uygunluğu Teyidi", d: "Her üründe tedarik durumu ve teslim süresi sipariş aşamasında doğrulanır." },
                  { icon: Truck, t: "Esnek Sevkiyat", d: "Uygun kargo veya nakliye; mümkün olan ürünlerde doğrudan tedarikçiden sevkiyat." },
                  { icon: Clock, t: "Net Teklif Koşulları", d: "Geçerlilik, ödeme, teslim ve sevkiyat her teklifte açıkça belirtilir." },
                ].map((x, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center shrink-0">
                      <x.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-head font-semibold text-slate-900">{x.t}</h4>
                      <p className="text-sm text-slate-500 mt-0.5">{x.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* İletişim — contact cards */}
        {slug === "iletisim" && (
          <>
            <div className="grid sm:grid-cols-3 gap-4 mt-12">
              <ContactCard icon={Phone} title="Telefon" value={company.telefon || "Teklif üzerinden iletişim"}
                href={company.telefon ? telHref(company.telefon) : null} testid="contact-phone-link" />
              <ContactCard icon={Mail} title="E-posta" value={company.email || "Teklif talebiyle iletişim"}
                href={company.email ? `mailto:${company.email}` : null} testid="contact-email-link" />
              <ContactCard icon={MapPin} title="Adres" value={company.adres || "Türkiye"} />
            </div>
            <div data-testid="sevkiyat-noktalari-bilgi" className="mt-4 bg-teal-50 border border-teal-100 rounded-xl p-6 flex gap-4">
              <div className="w-11 h-11 shrink-0 rounded-lg bg-white text-teal-600 flex items-center justify-center">
                <Truck className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-head font-semibold text-slate-900">Sevkiyat Noktaları</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Ürün çeşidine göre sevkiyatlar aşağıdaki farklı tedarik noktalarından yapılmaktadır.
                </p>
                <p data-testid="sevkiyat-noktalari-liste" className="text-sm font-semibold text-teal-700 mt-1">
                  Sevk Noktaları: İSTANBUL, İZMİR, BURSA, KOCAELİ, GAZİANTEP
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ContactCard({ icon: Icon, title, value, href, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6">
      <div className="w-11 h-11 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center mb-3">
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="font-head font-semibold text-slate-900">{title}</h3>
      {href ? (
        <a href={href} data-testid={testid}
          className="text-sm text-teal-700 font-medium mt-1 break-words inline-block hover:underline">{value}</a>
      ) : (
        <p className="text-sm text-slate-600 mt-1 break-words">{value}</p>
      )}
    </div>
  );
}
